import { Card, CardContent } from '@/components/ui/card';
import { Banknote, CalendarClock, Percent, Receipt, FileCheck2, ShieldCheck } from 'lucide-react';
import { LOAN_TERMS } from '@/config/company';

const facts = [
  { icon: Banknote, label: 'Loan amounts', value: '$1,000 – $50,000' },
  { icon: CalendarClock, label: 'Repayment terms', value: '12 – 60 months' },
  { icon: Percent, label: 'APR range (fixed)', value: `${LOAN_TERMS.minApr}% – ${LOAN_TERMS.maxApr}%` },
  { icon: Receipt, label: 'Origination fee', value: '0.00% – 8.00% of loan amount' },
];

const fees = [
  ['Origination fee', 'Deducted from the loan amount before disbursement. Disclosed in your offer.'],
  ['Late payment fee', 'Up to $15, charged if a payment is more than 15 days past due.'],
  ['Returned payment fee', 'Up to $15 if a payment is returned unpaid by your bank.'],
  ['Early repayment', 'No prepayment penalty. Pay off your balance early at any time.'],
];

export default function LoanDetails() {
  return (
    <section id="loan-details" className="py-20">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
            Transparent Pricing
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            Loan amounts, terms and costs
          </h2>
          <p className="text-muted-foreground text-lg">
            Everything you need to compare our personal loans, shown up front — before you apply.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto mb-10">
          {facts.map((f) => (
            <Card key={f.label} className="shadow-card">
              <CardContent className="p-6">
                <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                  <f.icon className="h-5 w-5 text-accent" />
                </div>
                <p className="text-sm text-muted-foreground mb-1">{f.label}</p>
                <p className="font-display text-lg font-bold text-foreground">{f.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <Card className="shadow-card">
            <CardContent className="p-6 md:p-8">
              <h3 className="font-display text-xl font-bold text-foreground mb-4">
                Representative repayment example
              </h3>
              <p className="text-muted-foreground text-sm mb-5">
                Based on a representative {LOAN_TERMS.repApr}% APR (fixed).
              </p>
              <dl className="space-y-3 text-sm">
                {[
                  ['Amount borrowed', '$10,000'],
                  ['Term', '36 months'],
                  ['Representative APR', `${LOAN_TERMS.repApr}% (fixed)`],
                  ['Monthly repayment', '$365.66'],
                  ['Total interest', '$3,163.76'],
                  ['Total amount repayable', '$13,163.76'],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-border pb-2 last:border-0">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="font-medium text-foreground">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="text-xs text-muted-foreground mt-5 leading-relaxed">
                Example is illustrative only and excludes any origination fee. Rates offered depend on
                your credit profile, income, loan amount and term. Not all applicants will qualify.
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardContent className="p-6 md:p-8">
              <h3 className="font-display text-xl font-bold text-foreground mb-4">Fees you may pay</h3>
              <ul className="space-y-4">
                {fees.map(([title, desc]) => (
                  <li key={title} className="flex gap-3">
                    <FileCheck2 className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground text-sm">{title}</p>
                      <p className="text-sm text-muted-foreground">{desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="flex items-start gap-2 text-xs text-muted-foreground mt-6">
                <ShieldCheck className="h-4 w-4 flex-shrink-0 mt-0.5" />
                All applicable fees and your total cost of credit are disclosed in your loan agreement
                before you sign. Borrow only what you can comfortably repay.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
